import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Star, Upload, ShieldCheck, ShieldX, ExternalLink, Ban } from "lucide-react";
import { toast } from "sonner";
import { Field, StatusBadge, DataTable, Kpi, KV } from "@/components/partners/common";
import { Sel } from "@/components/partners/PartnerForm";
import { KIND, titleCase, fmtMoney, fmtNum, fmtDate, fmtDateTime, apiError, fieldErrors } from "@/lib/partnerApi";
import { CURRENCIES, currencySymbol } from "@/lib/currency";

function useList(url) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await api.get(url)).data); } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [url]);
  useEffect(() => { load(); }, [load]);
  return [rows, load, loading];
}

/** Small modal form: fields = [{key,label,type,options,required,span}] */
export function EditDialog({ open, onOpenChange, title, fields, initial, onSubmit, submitLabel = "Save" }) {
  const [f, setF] = useState({});
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setF(initial || {}); setErrors({}); } }, [open, initial]);
  const submit = async () => {
    setBusy(true); setErrors({});
    try { await onSubmit(f); onOpenChange(false); } catch (e) { setErrors(fieldErrors(e)); toast.error(apiError(e, "Save failed")); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {fields.map((fd) => (
            <Field key={fd.key} label={fd.label} required={fd.required} error={errors[fd.key]} className={fd.span ? "col-span-2" : ""}>
              {fd.type === "select" ? (
                <Sel value={f[fd.key]} onChange={(v) => setF({ ...f, [fd.key]: v })} options={fd.options} tid={`dlg-${fd.key}`} />
              ) : fd.type === "bool" ? (
                <label className="flex items-center gap-2 text-sm h-9"><input type="checkbox" checked={!!f[fd.key]} onChange={(e) => setF({ ...f, [fd.key]: e.target.checked })} /> Yes</label>
              ) : fd.type === "textarea" ? (
                <Textarea rows={2} value={f[fd.key] ?? ""} onChange={(e) => setF({ ...f, [fd.key]: e.target.value })} />
              ) : (
                <Input type={fd.type || "text"} value={f[fd.key] ?? ""} onChange={(e) => setF({ ...f, [fd.key]: fd.type === "number" && e.target.value !== "" ? Number(e.target.value) : e.target.value })}
                  data-testid={`dlg-${fd.key}`} placeholder={fd.placeholder} />
              )}
            </Field>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={submit} disabled={busy} data-testid="dlg-save">{submitLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChildTab({ kind, pid, meta, canEdit, onChanged, child, fields, columns, noun }) {
  const base = `${KIND[kind].api}/${pid}/${child}`;
  const [rows, load] = useList(base);
  const [edit, setEdit] = useState(null);
  const save = async (f) => {
    if (edit?.id) await api.put(`${base}/${edit.id}`, f); else await api.post(base, f);
    toast.success(`${noun} saved`); load(); onChanged?.();
  };
  const remove = async (r) => {
    if (!window.confirm(`Deactivate this ${noun.toLowerCase()}? It stays in the audit history.`)) return;
    try { await api.delete(`${base}/${r.id}`); toast.success(`${noun} deactivated`); load(); onChanged?.(); } catch (e) { toast.error(apiError(e)); }
  };
  const cols = [
    ...columns,
    { key: "is_primary", label: "Primary", render: (r) => (r.is_primary ? <Star className="w-4 h-4 fill-amber-400 text-amber-400" /> : "") },
    ...(canEdit ? [{ key: "_a", label: "", render: (r) => (
      <div className="flex justify-end">
        <Button variant="ghost" size="icon" onClick={() => setEdit(r)}><Pencil className="w-4 h-4 text-slate-500" /></Button>
        <Button variant="ghost" size="icon" onClick={() => remove(r)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
      </div>) }] : []),
  ];
  return (
    <div className="space-y-3">
      {canEdit && <Button size="sm" onClick={() => setEdit({ is_primary: rows.length === 0 })} data-testid={`add-${child}-btn`}><Plus className="w-4 h-4 mr-1" /> Add {noun}</Button>}
      <DataTable columns={cols} rows={rows} empty={`No ${noun.toLowerCase()}s yet.`} tid={`${child}-table`} />
      <EditDialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)} title={`${edit?.id ? "Edit" : "Add"} ${noun}`}
        fields={fields(meta)} initial={edit} onSubmit={save} />
    </div>
  );
}

export function ContactsTab(props) {
  return (
    <ChildTab {...props} child="contacts" noun="Contact"
      fields={(meta) => [
        { key: "contact_role", label: "Contact Type", type: "select", options: meta.contact_roles, required: true },
        { key: "name", label: "Contact Person Name", required: true },
        { key: "designation", label: "Designation" }, { key: "department", label: "Department" },
        { key: "mobile", label: "Mobile Number" }, { key: "alt_mobile", label: "Alternate Mobile" },
        { key: "email", label: "Email Address" }, { key: "alt_email", label: "Alternate Email" },
        { key: "whatsapp", label: "WhatsApp Number" }, { key: "landline", label: "Landline" },
        { key: "fax", label: "Fax" }, { key: "is_primary", label: "Primary contact", type: "bool" },
      ]}
      columns={[
        { key: "contact_role", label: "Type" }, { key: "name", label: "Name" }, { key: "designation", label: "Designation" },
        { key: "department", label: "Department" }, { key: "mobile", label: "Mobile" }, { key: "email", label: "Email" },
        { key: "whatsapp", label: "WhatsApp" }, { key: "landline", label: "Landline" },
      ]} />
  );
}

export function AddressesTab(props) {
  return (
    <ChildTab {...props} child="addresses" noun="Address"
      fields={(meta) => [
        { key: "address_type", label: "Address Type", type: "select", options: meta.address_types, required: true },
        { key: "label", label: "Location Label", placeholder: "e.g. Hosur Warehouse" },
        { key: "line1", label: "Address Line 1", required: true, span: true }, { key: "line2", label: "Address Line 2", span: true },
        { key: "area", label: "Area" }, { key: "city", label: "City", required: true },
        { key: "state", label: "State", required: true }, { key: "country", label: "Country", required: true },
        { key: "pin", label: "PIN / ZIP" }, { key: "gst_state_code", label: "GST State Code", placeholder: "auto from state" },
        { key: "gstin", label: "Location GSTIN (if different)" }, { key: "contact_name", label: "Location Contact" },
        { key: "contact_phone", label: "Location Phone" }, { key: "is_primary", label: "Primary for this type", type: "bool" },
      ]}
      columns={[
        { key: "address_type", label: "Type" }, { key: "label", label: "Label" },
        { key: "line1", label: "Address", render: (r) => [r.line1, r.line2, r.area].filter(Boolean).join(", ") },
        { key: "city", label: "City" }, { key: "state", label: "State" }, { key: "pin", label: "PIN" },
        { key: "gst_state_code", label: "GST Code" },
      ]} />
  );
}

export function BankTab({ kind, pid, meta, canEdit }) {
  const base = `${KIND[kind].api}/${pid}/bank-accounts`;
  const [rows, load] = useList(base);
  const [edit, setEdit] = useState(null);
  const save = async (f) => {
    const body = { ...f };
    ["id", "partner_id", "partner_kind", "approval_status", "pending_change", "created_at", "created_by", "is_active", "approved_by", "approved_at"].forEach((k) => delete body[k]);
    if (edit?.id) {
      if (body.account_number && body.account_number.includes("X")) delete body.account_number;
      await api.put(`${base}/${edit.id}`, body);
    } else await api.post(base, body);
    toast.success("Submitted for approval — the change applies once Accounts approves it"); load();
  };
  const remove = async (r) => {
    if (!window.confirm("Deactivate this bank account?")) return;
    try { await api.delete(`${base}/${r.id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">Every new account or change goes to <b>Bank Detail Verification</b> approval and is logged in the audit history. Payments use only approved accounts.</p>
      {canEdit && <Button size="sm" onClick={() => setEdit({ currency: "INR", payment_method: "bank_transfer", account_type: "current" })} data-testid="add-bank-btn"><Plus className="w-4 h-4 mr-1" /> Add Bank Account</Button>}
      <DataTable tid="bank-table" rows={rows} empty="No bank accounts." columns={[
        { key: "bank_name", label: "Bank" }, { key: "branch", label: "Branch" }, { key: "account_holder", label: "Holder" },
        { key: "account_number", label: "Account No.", render: (r) => <span className="mono">{r.account_number}</span> },
        { key: "account_type", label: "Type" }, { key: "ifsc", label: "IFSC", render: (r) => <span className="mono">{r.ifsc || r.swift || "—"}</span> },
        { key: "upi_id", label: "UPI" }, { key: "currency", label: "Cur" }, { key: "payment_method", label: "Method" },
        { key: "is_default", label: "Default", render: (r) => (r.is_default ? "Yes" : "") },
        { key: "approval_status", label: "Approval", render: (r) => (
          <div><StatusBadge value={r.approval_status} />{r.pending_change && <div className="text-[10px] text-amber-700 mt-0.5">change pending</div>}</div>) },
        ...(canEdit ? [{ key: "_a", label: "", render: (r) => (
          <div className="flex justify-end">
            <Button variant="ghost" size="icon" disabled={r.approval_status === "pending"} onClick={() => setEdit(r)}><Pencil className="w-4 h-4 text-slate-500" /></Button>
            <Button variant="ghost" size="icon" onClick={() => remove(r)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
          </div>) }] : []),
      ]} />
      <EditDialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)} title={edit?.id ? "Change Bank Account" : "Add Bank Account"}
        submitLabel="Submit for approval" initial={edit} onSubmit={save} fields={[
          { key: "bank_name", label: "Bank Name", required: true }, { key: "branch", label: "Branch Name" },
          { key: "account_holder", label: "Account Holder Name", required: true },
          { key: "account_type", label: "Account Type", type: "select", options: meta.account_types },
          { key: "account_number", label: "Account Number" }, { key: "confirm_account_number", label: "Confirm Account Number" },
          { key: "ifsc", label: "IFSC Code" }, { key: "swift", label: "SWIFT Code" }, { key: "iban", label: "IBAN" },
          { key: "upi_id", label: "UPI ID" }, { key: "currency", label: "Currency" },
          { key: "payment_method", label: "Payment Method", type: "select", options: meta.payment_methods },
          { key: "is_default", label: "Default bank account", type: "bool" },
        ]} />
    </div>
  );
}

export function DocumentsTab({ kind, pid, meta, canEdit, canApprove }) {
  const base = `${KIND[kind].api}/${pid}/documents`;
  const [rows, load] = useList(base);
  const [f, setF] = useState({ doc_type: "", doc_number: "", issue_date: "", expiry_date: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const upload = async () => {
    if (!file || !f.doc_type) { toast.error("Choose a document type and a file"); return; }
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(f).forEach(([k, v]) => fd.append(k, v || ""));
    setBusy(true);
    try {
      await api.post(base, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded — pending approval"); setFile(null); setF({ doc_type: "", doc_number: "", issue_date: "", expiry_date: "" }); load();
    } catch (e) { toast.error(apiError(e, "Upload failed")); } finally { setBusy(false); }
  };
  const decide = async (d, ok) => {
    const remarks = ok ? "" : window.prompt("Reason for rejection?") || "";
    try { await api.post(`${base}/${d.id}/${ok ? "approve" : "reject"}`, { remarks }); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const view = async (d) => {
    try {
      const { data } = await api.get(`${base}/${d.id}/file`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([data], { type: d.content_type })), "_blank");
    } catch (e) { toast.error(apiError(e, "Could not open file")); }
  };
  const remove = async (d) => {
    if (!window.confirm("Deactivate this document?")) return;
    try { await api.delete(`${base}/${d.id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-4">
      {canEdit && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 grid grid-cols-2 md:grid-cols-6 gap-3 items-end" data-testid="doc-upload">
          <Field label="Document Type" required><Sel value={f.doc_type} onChange={(v) => setF({ ...f, doc_type: v })} options={meta.document_types} tid="doc-type" /></Field>
          <Field label="Document Number"><Input value={f.doc_number} onChange={(e) => setF({ ...f, doc_number: e.target.value })} /></Field>
          <Field label="Issue Date"><Input type="date" value={f.issue_date} onChange={(e) => setF({ ...f, issue_date: e.target.value })} /></Field>
          <Field label="Expiry Date"><Input type="date" value={f.expiry_date} onChange={(e) => setF({ ...f, expiry_date: e.target.value })} /></Field>
          <Field label="File (PDF/Image, ≤10MB)"><Input type="file" accept=".pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="doc-file" /></Field>
          <Button onClick={upload} disabled={busy} data-testid="doc-upload-btn"><Upload className="w-4 h-4 mr-1" /> Upload</Button>
        </div>
      )}
      <DataTable tid="documents-table" rows={rows} empty="No documents uploaded." columns={[
        { key: "doc_type", label: "Type" }, { key: "doc_number", label: "Number" },
        { key: "issue_date", label: "Issued", type: "date" }, { key: "expiry_date", label: "Expiry", type: "date" },
        { key: "state", label: "Validity", render: (r) => <StatusBadge value={r.state} /> },
        { key: "file_name", label: "File", render: (r) => <button type="button" className="text-blue-600 hover:underline inline-flex items-center gap-1" onClick={() => view(r)}>{r.file_name}<ExternalLink className="w-3 h-3" /></button> },
        { key: "uploaded_by", label: "Uploaded By" }, { key: "uploaded_at", label: "Uploaded", type: "date" },
        { key: "approval_status", label: "Approval", render: (r) => <StatusBadge value={r.approval_status} /> },
        { key: "_a", label: "", render: (r) => (
          <div className="flex justify-end">
            {canApprove && r.approval_status === "pending" && (
              <>
                <Button variant="ghost" size="icon" title="Approve" onClick={() => decide(r, true)}><ShieldCheck className="w-4 h-4 text-emerald-600" /></Button>
                <Button variant="ghost" size="icon" title="Reject" onClick={() => decide(r, false)}><ShieldX className="w-4 h-4 text-red-500" /></Button>
              </>
            )}
            {canEdit && <Button variant="ghost" size="icon" onClick={() => remove(r)}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
          </div>) },
      ]} />
    </div>
  );
}

export function TransactionsTab({ kind, pid }) {
  const [tx, setTx] = useState(null);
  useEffect(() => { api.get(`${KIND[kind].api}/${pid}/transactions`).then((r) => setTx(r.data)).catch((e) => toast.error(apiError(e))); }, [kind, pid]);
  if (!tx) return <div className="text-sm text-slate-500">Loading…</div>;
  const groups = Object.entries(tx.groups).filter(([, rows]) => rows.length);
  return (
    <div className="space-y-5" data-testid="transactions-tab">
      <div className="flex flex-wrap gap-2">
        {Object.entries(tx.counts).map(([k, n]) => (
          <span key={k} className={`text-xs px-2.5 py-1 rounded-full border ${n ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-white border-slate-200 text-slate-400"}`}>{titleCase(k)} · {n}</span>
        ))}
      </div>
      {kind === "dealer" && (tx.reservations || []).length > 0 && (
        <div>
          <div className="text-sm font-semibold text-slate-700 mb-2">Stock Reserved for this Dealer's Open Orders</div>
          <DataTable rows={tx.reservations} columns={[
            { key: "order_no", label: "Order" }, { key: "item_code", label: "Item" }, { key: "item_name", label: "Description" },
            { key: "ordered_qty", label: "Ordered", type: "number" }, { key: "delivered_qty", label: "Delivered", type: "number" },
            { key: "reserved_qty", label: "Reserved", type: "number" }, { key: "item_available_qty", label: "Item Available (unreserved)", type: "number" },
          ]} />
        </div>
      )}
      {groups.length === 0 && <div className="text-sm text-slate-500">No transactions yet.</div>}
      {groups.map(([k, rows]) => (
        <div key={k}>
          <div className="text-sm font-semibold text-slate-700 mb-2">{rows[0].doc_type} <span className="text-slate-400 font-normal">({rows.length})</span></div>
          <DataTable rows={rows} maxHeight={320} columns={[
            { key: "number", label: "Number", render: (r) => (r.link ? <Link to={r.link} className="text-blue-600 hover:underline mono">{r.number}</Link> : <span className="mono">{r.number}</span>) },
            { key: "date", label: "Date", type: "date" }, { key: "amount", label: "Amount", type: "money" },
            { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
          ]} />
        </div>
      ))}
    </div>
  );
}

export function OutstandingTab({ kind, pid, meta }) {
  const api_ = `${KIND[kind].api}/${pid}`;
  const [o, setO] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [credit, setCredit] = useState(null);
  const [entry, setEntry] = useState(null);
  const [check, setCheck] = useState("");
  const [checkRes, setCheckRes] = useState(null);
  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([api.get(`${api_}/outstanding`), api.get(`${api_}/ledger`)]);
      setO(a.data); setLedger(b.data);
      if (kind === "dealer") setCredit((await api.get(`${api_}/credit`)).data);
    } catch (e) { toast.error(apiError(e)); }
  }, [api_, kind]);
  useEffect(() => { load(); }, [load]);
  if (!o) return <div className="text-sm text-slate-500">Loading…</div>;
  // Outstanding / credit figures are kept in the partner's own currency.
  const cur = o.currency || "INR";
  const money = (v) => fmtMoney(v, cur);
  const cancel = async (e) => {
    const reason = window.prompt("Reason for cancelling this entry?");
    if (!reason) return;
    try { await api.post(`${api_}/ledger/${e.id}/cancel`, { reason }); load(); } catch (x) { toast.error(apiError(x)); }
  };
  const runCheck = async () => {
    try { setCheckRes((await api.post(`${api_}/credit-check`, { amount: Number(check || 0) })).data); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-5" data-testid="outstanding-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {kind === "supplier" ? <Kpi label="Total Purchase Value" value={money(o.total_purchase_value)} /> : <Kpi label="Total Sales Invoiced" value={money(o.total_sales_invoiced)} />}
        <Kpi label="Total Invoiced" value={money(o.total_invoiced)} />
        <Kpi label={kind === "supplier" ? "Total Paid" : "Total Received"} value={money(o.total_paid)} />
        <Kpi label="Outstanding" value={money(o.outstanding)} tone="text-blue-700" />
        <Kpi label="Overdue" value={money(o.overdue)} tone={o.overdue > 0 ? "text-red-600" : "text-slate-900"} />
        <Kpi label="Next Due" value={fmtDate(o.next_due_date)} />
        <Kpi label="Credit Limit" value={o.credit_limit ? money(o.credit_limit) : "Not set"} />
        {kind === "dealer" && <Kpi label="Pending Orders" value={money(o.pending_orders_value)} />}
        <Kpi label="Used Credit" value={money(o.used_credit)} />
        <Kpi label="Available Credit" value={o.available_credit === null ? "—" : money(o.available_credit)} tone={o.available_credit === 0 ? "text-red-600" : "text-emerald-700"} />
        {o.advance > 0 && <Kpi label="Advance / On Account" value={money(o.advance)} />}
      </div>
      <div>
        <div className="text-sm font-semibold text-slate-700 mb-2">Invoice Aging</div>
        <div className="grid grid-cols-5 gap-2">
          {Object.entries(o.aging_labels).map(([k, label]) => (
            <div key={k} className={`rounded-md border p-3 ${k === "current" ? "border-slate-200 bg-white" : o.aging[k] > 0 ? "border-red-200 bg-red-50" : "border-slate-200 bg-white"}`}>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
              <div className="text-sm font-bold mono mt-1">{money(o.aging[k])}</div>
            </div>
          ))}
        </div>
      </div>
      {kind === "dealer" && credit && (
        <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3" data-testid="credit-panel">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm font-semibold text-slate-700">Credit Management <span className="text-xs font-normal text-slate-500">· policy: {titleCase(credit.mode)} when exceeded</span></div>
            <StatusBadge value={credit.exceeded ? "blocked" : "active"} />
          </div>
          <div className="text-xs text-slate-600">Rule: Current Outstanding + Pending Orders + New Order Value ≤ Credit Limit</div>
          <div className="flex items-end gap-2">
            <Field label={`Try an order value (${currencySymbol(cur)})`}><Input type="number" value={check} onChange={(e) => setCheck(e.target.value)} className="w-48" data-testid="credit-try-input" /></Field>
            <Button variant="outline" size="sm" onClick={runCheck}>Check</Button>
            {checkRes && (
              <div className={`text-sm ${checkRes.exceeded ? "text-red-600" : "text-emerald-700"}`}>
                {checkRes.exceeded ? `Exceeds by ${money(checkRes.excess)} → ${credit.mode === "warn" ? "warning only" : credit.mode === "block" ? "order blocked" : "needs credit approval"}` : `Within limit (exposure ${money(checkRes.exposure)})`}
              </div>
            )}
          </div>
          {credit.overrides?.length > 0 && (
            <DataTable rows={credit.overrides} columns={[
              { key: "code", label: "Override" }, { key: "requested_by", label: "Requested By" }, { key: "requested_at", label: "Requested", type: "date" },
              { key: "excess", label: "Excess", render: (r) => money(r.payload?.excess) }, { key: "status", label: "Status" },
              { key: "consumed", label: "Used", render: (r) => (r.consumed ? `Yes${r.consumed_by_ref ? ` (${r.consumed_by_ref})` : ""}` : "No") },
            ]} />
          )}
        </div>
      )}
      <div>
        <div className="text-sm font-semibold text-slate-700 mb-2">Open Items</div>
        <DataTable rows={o.open_items.map((r) => ({ ...r, currency: cur }))} empty="Nothing outstanding." columns={[
          { key: "ref", label: "Document" }, { key: "source", label: "Type" }, { key: "date", label: "Date", type: "date" },
          { key: "due_date", label: "Due", type: "date" }, { key: "amount", label: "Amount", type: "money" },
          { key: "paid", label: "Settled", type: "money" }, { key: "balance", label: "Balance", type: "money" },
          { key: "overdue_days", label: "Days Overdue", type: "number" }, { key: "bucket", label: "Bucket", render: (r) => o.aging_labels[r.bucket] },
        ]} />
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-700">Sub-ledger ({kind === "supplier" ? "purchase invoices, payments, notes, returns" : "receipts, notes, returns — sales invoices come from Tax Invoices"})</div>
          <Button size="sm" onClick={() => setEntry({ entry_type: meta.ledger_types[0], date: new Date().toISOString().slice(0, 10), currency: cur })} data-testid="add-ledger-btn"><Plus className="w-4 h-4 mr-1" /> Record Entry</Button>
        </div>
        <DataTable rows={ledger} empty="No entries." columns={[
          { key: "date", label: "Date", type: "date" }, { key: "entry_type", label: "Type" }, { key: "doc_no", label: "Doc No" },
          { key: "amount", label: "Amount", type: "money", render: (r) => <span className={r.sign < 0 ? "text-emerald-700" : ""}>{r.sign < 0 ? "−" : ""}{money(r.amount)}</span> },
          { key: "due_date", label: "Due", type: "date" }, { key: "reference", label: "Reference" }, { key: "payment_method", label: "Mode" },
          { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
          { key: "_a", label: "", render: (r) => (r.status === "posted" ? <Button variant="ghost" size="icon" title="Cancel entry" onClick={() => cancel(r)}><Ban className="w-4 h-4 text-red-500" /></Button> : null) },
        ]} />
      </div>
      <EditDialog open={!!entry} onOpenChange={(v) => !v && setEntry(null)} title="Record Ledger Entry" initial={entry}
        onSubmit={async (f) => { await api.post(`${api_}/ledger`, f); toast.success("Entry posted"); load(); }}
        fields={[
          { key: "entry_type", label: "Entry Type", type: "select", options: meta.ledger_types, required: true },
          { key: "doc_no", label: "Document No." }, { key: "date", label: "Date", type: "date" },
          { key: "due_date", label: "Due Date (debits)", type: "date" }, { key: "amount", label: "Amount", type: "number", required: true },
          { key: "currency", label: "Currency", type: "select", options: [{ value: cur, label: cur }] }, { key: "reference", label: "Reference (PO / GRN / Invoice)" },
          { key: "payment_method", label: "Payment Mode", type: "select", options: meta.payment_methods },
          { key: "notes", label: "Notes", type: "textarea", span: true },
        ]} />
    </div>
  );
}

export function PerformanceTab({ kind, pid, canEdit, currency = "INR" }) {
  const base = `${KIND[kind].api}/${pid}`;
  const [p, setP] = useState(null);
  const [dlg, setDlg] = useState(null);
  const load = useCallback(async () => { try { setP((await api.get(`${base}/performance`)).data); } catch (e) { toast.error(apiError(e)); } }, [base]);
  useEffect(() => { load(); }, [load]);
  if (!p) return <div className="text-sm text-slate-500">Loading…</div>;
  const k = p.kpis;
  // KPIs are computed in the partner's own currency (other-currency documents excluded).
  const money = (v) => fmtMoney(v, currency);
  const pct = (v) => (v === null || v === undefined ? "—" : `${fmtNum(v, 1)}%`);
  const closeComplaint = async (c) => {
    const remarks = window.prompt("Resolution?");
    if (remarks === null) return;
    try { await api.post(`${base}/complaints/${c.id}/close`, { remarks }); load(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-5" data-testid="performance-tab">
      {kind === "supplier" ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <Kpi label="Performance Score" value={k.performance_score ?? "—"} tone={k.performance_score !== null && k.performance_score < 60 ? "text-red-600" : "text-emerald-700"} sub={k.rating ? `≈ ${k.rating} ★` : ""} />
          <Kpi label="On-Time Delivery" value={pct(k.on_time_delivery_pct)} />
          <Kpi label="Quality Acceptance" value={pct(k.quality_acceptance_pct)} />
          <Kpi label="Rejection" value={pct(k.rejection_pct)} />
          <Kpi label="Avg Lead Time" value={k.average_lead_time_days !== null ? `${k.average_lead_time_days} d` : "—"} />
          <Kpi label="Avg Delay" value={`${k.average_delay_days || 0} d`} />
          <Kpi label="Price Variance" value={pct(k.purchase_price_variance_pct)} />
          <Kpi label="Purchase Orders" value={k.po_count} />
          <Kpi label="Late Deliveries" value={k.late_deliveries} />
          <Kpi label="Rejections" value={k.rejections} />
          <Kpi label="Complaints" value={k.complaints} />
          <Kpi label="Total Purchase Value" value={money(k.total_purchase_value)} />
          <Kpi label="Last Evaluation" value={fmtDate(k.last_evaluation_date)} />
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {"total_sales" in k && <>
            <Kpi label="Total Sales" value={money(k.total_sales)} />
            <Kpi label="This Month" value={money(k.monthly_sales)} />
            <Kpi label="This Year" value={money(k.yearly_sales)} />
            <Kpi label="Month Target" value={money(k.sales_target_month)} />
            <Kpi label="Target Achievement" value={pct(k.target_achievement_pct)} />
            <Kpi label="Outstanding" value={money(k.outstanding_amount)} />
            <Kpi label="Collection Efficiency" value={pct(k.collection_efficiency_pct)} />
            <Kpi label="Avg Order Value" value={money(k.average_order_value)} />
            <Kpi label="Returns" value={money(k.returns_value)} />
            <Kpi label="Sales Growth" value={pct(k.sales_growth_pct)} />
            <Kpi label="Last Payment" value={fmtDate(k.last_payment_date)} />
          </>}
          <Kpi label="Orders" value={k.order_count} sub={k.order_frequency_per_month ? `${k.order_frequency_per_month}/month` : ""} />
          <Kpi label="Complaints" value={k.complaints} />
          <Kpi label="Last Order" value={fmtDate(k.last_order_date)} />
        </div>
      )}
      {kind === "dealer" && k.monthly_trend?.length > 0 && (
        <div>
          <div className="text-sm font-semibold text-slate-700 mb-2">Monthly Sales</div>
          <div className="flex items-end gap-1 h-28 bg-white border border-slate-200 rounded-lg p-3">
            {k.monthly_trend.map((m) => {
              const max = Math.max(...k.monthly_trend.map((x) => x.sales)) || 1;
              return (
                <div key={m.month} className="flex-1 flex flex-col items-center justify-end h-full" title={`${m.month}: ${money(m.sales)}`}>
                  <div className="w-full bg-blue-500 rounded-t" style={{ height: `${Math.max(3, (m.sales / max) * 100)}%` }} />
                  <div className="text-[9px] text-slate-500 mt-1">{m.month.slice(2)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className="flex gap-2 flex-wrap">
        {canEdit && <Button size="sm" onClick={() => setDlg("eval")} data-testid="add-eval-btn">Record Evaluation</Button>}
        <Button size="sm" variant="outline" onClick={() => setDlg("complaint")}>Log Complaint</Button>
        {kind === "dealer" && canEdit && <Button size="sm" variant="outline" onClick={() => setDlg("target")}>Set Sales Target</Button>}
      </div>
      {kind === "dealer" && p.targets?.length > 0 && (
        <DataTable rows={p.targets} columns={[{ key: "period", label: "Period" }, { key: "target_amount", label: "Target", type: "money" }, { key: "updated_by", label: "Set By" }]} />
      )}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <div className="text-sm font-semibold text-slate-700 mb-2">Evaluations</div>
          <DataTable rows={p.evaluations} empty="No evaluations recorded." columns={[
            { key: "created_at", label: "Date", type: "date" }, { key: "period", label: "Period" }, { key: "rating", label: "Rating", type: "number" },
            { key: "comments", label: "Comments" }, { key: "created_by", label: "By" },
          ]} />
        </div>
        <div>
          <div className="text-sm font-semibold text-slate-700 mb-2">Complaints</div>
          <DataTable rows={p.complaints} empty="No complaints." columns={[
            { key: "date", label: "Date", type: "date" }, { key: "subject", label: "Subject" },
            { key: "severity", label: "Severity", render: (r) => <StatusBadge value={r.severity} /> },
            { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
            { key: "_a", label: "", render: (r) => (r.status === "open" && canEdit ? <Button size="sm" variant="ghost" onClick={() => closeComplaint(r)}>Close</Button> : r.resolution || "") },
          ]} />
        </div>
      </div>
      <EditDialog open={dlg === "eval"} onOpenChange={(v) => !v && setDlg(null)} title="Record Evaluation" initial={{ period: new Date().toISOString().slice(0, 7) }}
        onSubmit={async (f) => { await api.post(`${base}/evaluations`, f); toast.success("Evaluation saved"); load(); }}
        fields={[{ key: "period", label: "Period (YYYY-MM or Qn)" }, { key: "rating", label: "Rating (0–5)", type: "number" },
          { key: "comments", label: "Evaluation Comments", type: "textarea", span: true, required: true }]} />
      <EditDialog open={dlg === "complaint"} onOpenChange={(v) => !v && setDlg(null)} title="Log Complaint" initial={{ severity: "medium" }}
        onSubmit={async (f) => { await api.post(`${base}/complaints`, f); toast.success("Complaint logged"); load(); }}
        fields={[{ key: "subject", label: "Subject", required: true, span: true }, { key: "severity", label: "Severity", type: "select", options: ["high", "medium", "low"] },
          { key: "reference", label: "Reference (PO / GIN / Invoice)" }, { key: "description", label: "Description", type: "textarea", span: true }]} />
      <EditDialog open={dlg === "target"} onOpenChange={(v) => !v && setDlg(null)} title="Set Monthly Sales Target" initial={{ period: new Date().toISOString().slice(0, 7) }}
        onSubmit={async (f) => { await api.post(`${base}/targets`, f); toast.success("Target saved"); load(); }}
        fields={[{ key: "period", label: "Month (YYYY-MM)", required: true }, { key: "target_amount", label: `Target (${currencySymbol(currency)})`, type: "number", required: true }]} />
    </div>
  );
}

export function SupplierItemsTab({ pid, canEdit, canPrice }) {
  const base = `/supplier-master/${pid}/items`;
  const [rows, load] = useList(base);
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  useEffect(() => { api.get("/items").then((r) => setItems(r.data)).catch(() => {}); }, []);
  const save = async (f) => {
    const body = { ...f };
    ["id", "supplier_id", "supplier_code", "supplier_name", "role", "effective_now", "item_code", "item_name", "created_at", "created_by",
      "last_purchase_price", "last_purchase_date", "is_active", "updated_at", "updated_by"].forEach((k) => delete body[k]);
    if (edit?.id) await api.put(`${base}/${edit.id}`, body); else await api.post(base, body);
    toast.success("Item mapping saved"); load();
  };
  const remove = async (r) => {
    if (!window.confirm(`Remove ${r.item_code} from this supplier?`)) return;
    try { await api.delete(`${base}/${r.id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">One item can have several suppliers — mark one <b>Preferred</b>; rank others Backup (2) / Emergency (3). MRP, low-stock PRs and PR→PO use this ranking, price, MOQ, packaging and lead time.</p>
      {canEdit && <Button size="sm" onClick={() => setEdit({ currency: "INR", priority_rank: 2 })} data-testid="add-mapping-btn"><Plus className="w-4 h-4 mr-1" /> Map Item</Button>}
      <DataTable tid="mapping-table" rows={rows} empty="No items mapped to this supplier." columns={[
        { key: "item_code", label: "Item Code", render: (r) => <span className="mono">{r.item_code}</span> }, { key: "item_name", label: "Item" },
        { key: "supplier_item_code", label: "Supplier Code" }, { key: "role", label: "Role", render: (r) => <StatusBadge value={r.role === "preferred" ? "approved" : r.role === "backup" ? "pending" : "draft"} /> },
        { key: "purchase_price", label: "Price", type: "money" }, { key: "uom", label: "UOM" }, { key: "moq", label: "MOQ", type: "number" },
        { key: "max_order_qty", label: "Max", type: "number" }, { key: "packaging_qty", label: "Pack", type: "number" },
        { key: "lead_time_days", label: "Lead (d)", type: "number" }, { key: "safety_lead_time_days", label: "Safety (d)", type: "number" },
        { key: "tax_rate", label: "Tax %", type: "number" }, { key: "effective_from", label: "From", type: "date" }, { key: "effective_to", label: "To", type: "date" },
        { key: "last_purchase_price", label: "Last PO Price", type: "money" }, { key: "last_purchase_date", label: "Last PO", type: "date" },
        ...(canEdit ? [{ key: "_a", label: "", render: (r) => (
          <div className="flex justify-end">
            <Button variant="ghost" size="icon" onClick={() => setEdit(r)}><Pencil className="w-4 h-4 text-slate-500" /></Button>
            <Button variant="ghost" size="icon" onClick={() => remove(r)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
          </div>) }] : []),
      ]} />
      <EditDialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)} title={edit?.id ? `Edit ${edit.item_code}` : "Map Item to Supplier"} initial={edit} onSubmit={save}
        fields={[
          { key: "item_id", label: "Item", type: "select", required: true, span: true, options: items.filter((i) => ["RAW", "SFG"].includes(i.type) || edit?.item_id === i.id).map((i) => ({ value: i.id, label: `${i.code} · ${i.name}` })) },
          { key: "supplier_item_code", label: "Supplier Item Code" }, { key: "supplier_item_description", label: "Supplier Item Description" },
          ...(canPrice || !edit?.id ? [{ key: "purchase_price", label: "Purchase Price", type: "number" }] : []), { key: "currency", label: "Currency", type: "select", options: CURRENCIES.map((c) => ({ value: c.code, label: c.label })) },
          { key: "uom", label: "UOM" }, { key: "tax_rate", label: "Tax Rate %", type: "number" },
          { key: "moq", label: "Minimum Order Qty", type: "number" }, { key: "max_order_qty", label: "Maximum Order Qty", type: "number" },
          { key: "min_purchase_qty", label: "Minimum Purchase Qty", type: "number" }, { key: "packaging_qty", label: "Packaging Qty", type: "number" },
          { key: "lead_time_days", label: "Lead Time (days)", type: "number" }, { key: "safety_lead_time_days", label: "Safety Lead Time (days)", type: "number" },
          { key: "standard_delivery_days", label: "Standard Delivery Days", type: "number" },
          { key: "priority_rank", label: "Supplier Priority", type: "select", options: [{ value: 1, label: "1 · Preferred" }, { value: 2, label: "2 · Backup" }, { value: 3, label: "3 · Emergency" }] },
          { key: "effective_from", label: "Effective From", type: "date" }, { key: "effective_to", label: "Effective To", type: "date" },
          { key: "is_preferred", label: "Preferred Supplier", type: "bool" },
        ]} />
    </div>
  );
}

export function DealerPricingTab({ pid, meta, canPrice, partner }) {
  const [data, setData] = useState(null);
  const [stock, setStock] = useState(null);
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [move, setMove] = useState(null);
  const load = useCallback(async () => {
    try { setData((await api.get(`/dealer-master/${pid}/pricing`)).data); } catch { setData({ dealer: [], level: [] }); }
    try { setStock((await api.get(`/dealer-master/${pid}/stock`)).data); } catch { /* no view */ }
  }, [pid]);
  useEffect(() => { load(); api.get("/items").then((r) => setItems(r.data)).catch(() => {}); }, [load]);
  const cols = [
    { key: "item_code", label: "Item", render: (r) => <span className="mono">{r.item_code}</span> }, { key: "item_name", label: "Description" },
    { key: "standard_price", label: "Standard", type: "money" }, { key: "dealer_price", label: "Dealer Price", type: "money" },
    { key: "discount_pct", label: "Disc %", type: "number" }, { key: "special_price", label: "Special", type: "money" },
    { key: "tax_pct", label: "Tax %", type: "number" }, { key: "min_qty", label: "Min Qty", type: "number" }, { key: "max_qty", label: "Max Qty", type: "number" },
    { key: "effective_from", label: "From", type: "date" }, { key: "effective_to", label: "To", type: "date" },
    { key: "effective_now", label: "Live", render: (r) => (r.effective_now ? "Yes" : "No") },
  ];
  const save = async (f) => {
    const body = { ...f, dealer_id: pid };
    ["id", "item_code", "item_name", "effective_now", "is_active", "created_at", "created_by", "updated_at", "updated_by", "price_level"].forEach((k) => delete body[k]);
    if (edit?.id) await api.put(`/dealer-master/pricing/${edit.id}`, body); else await api.post("/dealer-master/pricing", body);
    toast.success("Price saved"); load();
  };
  return (
    <div className="space-y-5">
      <div className="text-xs text-slate-500">Price used on orders: dealer-specific record → <b>{titleCase(partner.price_level) || "no level"}</b> price level → item standard price less the dealer's discount. Every change is audited.</div>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-700">Dealer-specific Prices</div>
          {canPrice && <Button size="sm" onClick={() => setEdit({ currency: "INR" })} data-testid="add-price-btn"><Plus className="w-4 h-4 mr-1" /> Add Price</Button>}
        </div>
        <DataTable rows={data?.dealer || []} empty="No dealer-specific prices." columns={[...cols,
          ...(canPrice ? [{ key: "_a", label: "", render: (r) => <Button variant="ghost" size="icon" onClick={() => setEdit(r)}><Pencil className="w-4 h-4 text-slate-500" /></Button> }] : [])]} />
      </div>
      {data?.level?.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-semibold text-slate-700">{titleCase(data.price_level)} Level Prices</div>
          <DataTable rows={data.level} columns={cols} />
        </div>
      )}
      {stock && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-700">Dealer Stock / Consignment</div>
            <Button size="sm" variant="outline" onClick={() => setMove({ movement_type: "transfer_in", ownership: "consignment", location: stock.locations?.[0] || "Main" })}>Record Movement</Button>
          </div>
          <DataTable rows={stock.balances} empty="No stock at this dealer." columns={[
            { key: "location", label: "Location" }, { key: "ownership", label: "Ownership" }, { key: "item_code", label: "Item" },
            { key: "item_name", label: "Description" }, { key: "qty", label: "Qty", type: "number" }, { key: "uom", label: "UOM" },
            { key: "last_movement", label: "Last Movement", type: "date" },
          ]} />
          {stock.movements.length > 0 && (
            <details className="text-sm"><summary className="cursor-pointer text-slate-600">Movement history ({stock.movements.length})</summary>
              <DataTable rows={stock.movements} columns={[
                { key: "date", label: "Date", type: "date" }, { key: "movement_type", label: "Movement" }, { key: "item_code", label: "Item" },
                { key: "location", label: "Location" }, { key: "ownership", label: "Ownership" }, { key: "qty", label: "Qty", type: "number" },
                { key: "reference", label: "Reference" }, { key: "created_by", label: "By" },
              ]} />
            </details>
          )}
        </div>
      )}
      <EditDialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)} title={edit?.id ? `Edit price ${edit.item_code}` : "Add Dealer Price"} initial={edit} onSubmit={save}
        fields={[
          { key: "item_id", label: "Item", type: "select", required: true, span: true, options: items.map((i) => ({ value: i.id, label: `${i.code} · ${i.name}` })) },
          { key: "standard_price", label: "Standard Price", type: "number" }, { key: "dealer_price", label: "Dealer Price", type: "number" },
          { key: "discount_pct", label: "Discount %", type: "number" }, { key: "special_price", label: "Special Price", type: "number" },
          { key: "tax_pct", label: "Tax %", type: "number" }, { key: "currency", label: "Currency", type: "select", options: CURRENCIES.map((c) => ({ value: c.code, label: c.label })) },
          { key: "min_qty", label: "Minimum Quantity", type: "number" }, { key: "max_qty", label: "Maximum Quantity", type: "number" },
          { key: "effective_from", label: "Effective From", type: "date" }, { key: "effective_to", label: "Effective To", type: "date" },
        ]} />
      <EditDialog open={!!move} onOpenChange={(v) => !v && setMove(null)} title="Record Dealer Stock Movement" initial={move}
        onSubmit={async (f) => { await api.post(`/dealer-master/${pid}/stock`, f); toast.success("Movement recorded"); load(); }}
        fields={[
          { key: "item_id", label: "Item", type: "select", required: true, span: true, options: items.map((i) => ({ value: i.id, label: `${i.code} · ${i.name}` })) },
          { key: "movement_type", label: "Movement", type: "select", options: meta.stock_movement_types, required: true },
          { key: "ownership", label: "Ownership", type: "select", options: meta.stock_ownership, required: true },
          { key: "location", label: "Location" }, { key: "qty", label: "Quantity", type: "number", required: true },
          { key: "date", label: "Date", type: "date" }, { key: "reference", label: "Reference (DC / Invoice)" },
          { key: "notes", label: "Notes", type: "textarea", span: true },
        ]} />
    </div>
  );
}

export function AuditTab({ kind, pid }) {
  const [rows, setRows] = useState([]);
  const [entity, setEntity] = useState("");
  useEffect(() => {
    api.get(`${KIND[kind].api}/${pid}/audit`, { params: { entity: entity || undefined } }).then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  }, [kind, pid, entity]);
  return (
    <div className="space-y-3" data-testid="audit-tab">
      <div className="w-60">
        <Sel value={entity} onChange={setEntity} placeholder="All changes" options={[
          "master", "contact", "address", "bank_account", "document", "credit_limit", "pricing", "item_mapping", "approval", "ledger",
          "evaluation", "complaint", "dealer_stock", "target", "export",
        ]} />
      </div>
      <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
        {rows.length === 0 && <div className="p-6 text-center text-sm text-slate-500">No history.</div>}
        {rows.map((a) => (
          <div key={a.id} className="p-3 text-sm">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs mono text-slate-500">{fmtDateTime(a.at)}</span>
              <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700">{titleCase(a.entity)}</span>
              <span className="font-semibold text-slate-800">{titleCase(a.action)}</span>
              <span className="text-xs text-slate-500">by {a.user_name || a.user_email} ({titleCase(a.user_role)})</span>
            </div>
            {a.reason && <div className="text-xs text-slate-600 mt-1">Reason: {a.reason}</div>}
            {a.changes?.length > 0 && (
              <table className="mt-1.5 text-xs w-full max-w-3xl">
                <tbody>
                  {a.changes.map((c, i) => (
                    <tr key={i} className="align-top">
                      <td className="pr-3 text-slate-500 whitespace-nowrap">{c.field}</td>
                      <td className="pr-3 text-red-600 line-through">{c.old === null || c.old === undefined || c.old === "" ? "∅" : String(c.old)}</td>
                      <td className="text-emerald-700">{c.new === null || c.new === undefined || c.new === "" ? "∅" : String(c.new)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function ApprovalsTab({ kind, pid, onChanged }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get(`${KIND[kind].api}/${pid}/approvals`).then((r) => setRows(r.data)).catch(() => {}), [kind, pid]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-3">
      {rows.length === 0 && <div className="text-sm text-slate-500">No approval requests yet.</div>}
      {rows.map((a) => <ApprovalCard key={a.id} a={a} onDone={() => { load(); onChanged?.(); }} />)}
    </div>
  );
}

export function ApprovalCard({ a, onDone, showParty }) {
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const act = async (ok) => {
    setBusy(true);
    try { await api.post(`/partner-master/approvals/${a.id}/${ok ? "approve" : "reject"}`, { remarks }); toast.success(ok ? "Approved" : "Rejected"); onDone?.(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`approval-${a.code}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="mono font-semibold">{a.code}</span>
          <span className="text-xs px-2 py-0.5 rounded bg-slate-100">{titleCase(a.type)}</span>
          {showParty && <Link to={`${KIND[a.partner_kind].base}/${a.partner_id}`} className="text-sm text-blue-600 hover:underline">{a.partner_code} · {a.partner_name}</Link>}
        </div>
        <StatusBadge value={a.status} />
      </div>
      <div className="text-xs text-slate-500 mt-1">Requested by {a.requested_by_name || a.requested_by} · {fmtDateTime(a.requested_at)}</div>
      {a.type === "credit_override" && (
        <div className="text-xs text-slate-700 mt-2 grid grid-cols-3 md:grid-cols-6 gap-2">
          <KV label="Limit" value={fmtMoney(a.payload.credit_limit)} /><KV label="Outstanding" value={fmtMoney(a.payload.current_outstanding)} />
          <KV label="Pending" value={fmtMoney(a.payload.pending_orders)} /><KV label="New Order" value={fmtMoney(a.payload.new_order_value)} />
          <KV label="Exposure" value={fmtMoney(a.payload.exposure)} /><KV label="Excess" value={fmtMoney(a.payload.excess)} />
        </div>
      )}
      {a.type === "bank_change" && a.payload?.changes?.length > 0 && (
        <div className="text-xs mt-2">{a.payload.changes.map((c, i) => <div key={i}><span className="text-slate-500">{c.field}:</span> <span className="line-through text-red-600">{String(c.old ?? "∅")}</span> → <span className="text-emerald-700">{String(c.new ?? "∅")}</span></div>)}</div>
      )}
      <ol className="mt-3 flex flex-wrap gap-2">
        {a.levels.map((lv) => (
          <li key={lv.level} className={`text-xs rounded-md border px-2.5 py-1.5 ${lv.status === "approved" ? "border-emerald-200 bg-emerald-50" : lv.status === "rejected" ? "border-red-200 bg-red-50" : a.status === "pending" && a.current_level === lv.level ? "border-amber-300 bg-amber-50" : "border-slate-200"}`}>
            <div className="font-semibold">{lv.level}. {lv.name}</div>
            <div className="text-slate-500">{lv.acted_by ? `${titleCase(lv.status)} by ${lv.acted_by_name || lv.acted_by}` : `Roles: ${lv.roles.map(titleCase).join(", ")}`}</div>
            {lv.remarks && <div className="text-slate-600 italic">“{lv.remarks}”</div>}
          </li>
        ))}
      </ol>
      {a.can_act && (
        <div className="mt-3 flex gap-2 items-center">
          <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Remarks (required to reject)" className="max-w-md h-9" />
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => act(true)} data-testid={`approve-${a.code}`}>Approve</Button>
          <Button size="sm" variant="outline" className="text-red-600 border-red-200" disabled={busy} onClick={() => act(false)}>Reject</Button>
        </div>
      )}
    </div>
  );
}

